/**
 * Typed client for the CRM backend, written to sit next to CRM.tsx without changing it.
 *
 * NOT TESTED against the running services or inside your app. Three things are assumptions you must check:
 *   1. Transport: the default adapter calls your apiCall({service, endpoint, method, headers, body}). Your file only shows the GET form,
 *      so the `body` key is a guess. If apiCall names it differently, change `apiCallTransport` (one place).
 *   2. Token: `getToken` must return the bearer token your central auth API issued for the signed-in user.
 *   3. Routes: they go through the gateway (port 5100) as /api/<service>/<entity>. The base address is whatever service 'crm' points to.
 *
 * Rows: backend records have a GUID id and a human number (AC-0001). Row.id is the GUID (needed for every call); Row.number is the number.
 * In CRM.tsx, show `number` in the "mono" id columns and keep `id` for relations.
 */

export type Val = string | number;
export interface Row { id: string; [k: string]: Val }
export type Method = "GET" | "POST" | "PATCH" | "DELETE";
export interface Transport {
  send(method: Method, path: string, body: unknown, headers: Record<string, string>): Promise<{ status: number; data: any }>;
}

export class CrmApiError extends Error {
  constructor(public status: number, public code: string, message: string, public fields: Record<string, string[]> = {}) {
    super(message);
  }
}

/** Adapter for the apiCall helper in ../../api/services. */
export function apiCallTransport(apiCall: (o: any) => Promise<any>, service = "crm"): Transport {
  return {
    async send(method, path, body, headers) {
      const res = await apiCall({ service, endpoint: path, method, headers, ...(body === undefined ? {} : { body }) });
      // apiCall's result shape is not known here: it is expected to return the parsed JSON, or an object with `error`.
      if (res && typeof res === "object" && "error" in res && res.error) {
        const e = res.error;
        throw new CrmApiError(Number(e.status) || 500, String(e.code ?? "error"), String(e.detail ?? e.message ?? e), e.errors ?? {});
      }
      return { status: 200, data: res };
    },
  };
}

/** Plain fetch transport, for when you call the gateway directly. */
export function fetchTransport(baseUrl: string): Transport {
  return {
    async send(method, path, body, headers) {
      const res = await fetch(baseUrl.replace(/\/$/, "") + "/" + path.replace(/^\//, ""), {
        method, headers: { "Content-Type": "application/json", ...headers }, body: body === undefined ? undefined : JSON.stringify(body),
      });
      const text = await res.text();
      const data = text ? JSON.parse(text) : null;
      if (!res.ok) throw new CrmApiError(res.status, data?.code ?? "error", data?.detail ?? res.statusText, data?.errors ?? {});
      return { status: res.status, data };
    },
  };
}

/* ------------------------------------------------------------------ field maps */

interface EntityMap {
  path: string;
  /** backend record -> Row */
  toRow(r: any): Row;
  /** Row values -> request body. Only fields present in v are sent, so a partial edit never blanks other fields. */
  toBody(v: Record<string, Val>): Record<string, unknown>;
}

const has = (v: Record<string, Val>, k: string) => Object.prototype.hasOwnProperty.call(v, k);
const str = (x: unknown): string => (x === null || x === undefined ? "" : String(x));
const date = (x: unknown): string => (x ? String(x).slice(0, 10) : "");
const nz = (x: unknown): Val => (typeof x === "number" ? x : x === null || x === undefined ? "" : String(x));
const guid = (x: Val | undefined): string | undefined => (x ? String(x) : undefined);
const base = (r: any): Row => ({ id: str(r.id), number: str(r.number), version: str(r.version) });

/**
 * OWNER: send ownerId ONLY when the person picked someone. Leave it out and the backend assigns by the assignment rules.
 * An empty value or "auto" means "let the system choose". The form in CRM.tsx makes Owner a required select; change it to optional with
 * an "Assign automatically" first option, otherwise every record looks like a deliberate choice and the rules never run.
 */
export function ownerBody(v: Record<string, Val>): Record<string, unknown> {
  const id = has(v, "ownerId") ? String(v.ownerId) : "";
  return id && id !== "auto" ? { ownerId: id } : {};
}

const pick = (v: Record<string, Val>, map: Record<string, string>, numeric: string[] = []): Record<string, unknown> => {
  const out: Record<string, unknown> = {};
  for (const [front, back] of Object.entries(map)) {
    if (!has(v, front)) continue;
    const raw = v[front];
    out[back] = numeric.includes(front) ? (raw === "" ? null : Number(raw)) : raw === "" ? null : raw;
  }
  return out;
};

export const ENTITIES: Record<string, EntityMap> = {
  accounts: {
    path: "api/customers/accounts",
    toRow: (r) => ({ ...base(r), name: str(r.name), industry: str(r.industry), city: str(r.city), employees: nz(r.employees), revenue: nz(r.annualRevenue), status: str(r.status), owner: str(r.ownerName), ownerId: str(r.ownerId) }),
    toBody: (v) => ({ ...pick(v, { name: "name", industry: "industry", city: "city", employees: "employees", revenue: "annualRevenue", status: "status" }, ["employees", "revenue"]), ...ownerBody(v) }),
  },
  contacts: {
    path: "api/customers/contacts",
    toRow: (r) => ({ ...base(r), name: str(r.name), title: str(r.title), account: str(r.accountId), email: str(r.email), phone: str(r.phone), status: str(r.status), owner: str(r.ownerName), ownerId: str(r.ownerId) }),
    toBody: (v) => ({ ...pick(v, { name: "name", title: "title", email: "email", phone: "phone", status: "status" }), ...(has(v, "account") ? { accountId: guid(v.account) ?? null } : {}), ...ownerBody(v) }),
  },
  leads: {
    path: "api/sales/leads",
    toRow: (r) => ({ ...base(r), name: str(r.name), title: str(r.title), company: str(r.company), source: str(r.source), email: str(r.email), phone: str(r.phone), budget: nz(r.budgetAmount), status: str(r.status), campaign: str(r.campaignId), owner: str(r.ownerName), ownerId: str(r.ownerId) }),
    toBody: (v) => ({ ...pick(v, { name: "name", title: "title", company: "company", source: "source", email: "email", phone: "phone", budget: "budgetAmount", status: "status" }, ["budget"]), ...(has(v, "campaign") ? { campaignId: guid(v.campaign) ?? null } : {}), ...ownerBody(v) }),
  },
  opportunities: {
    path: "api/sales/opportunities",
    toRow: (r) => ({ ...base(r), name: str(r.name), account: str(r.accountId), amount: nz(r.amount), stage: str(r.stage), probability: nz(r.probability), close: date(r.closeDate), owner: str(r.ownerName), ownerId: str(r.ownerId) }),
    toBody: (v) => ({ ...pick(v, { name: "name", amount: "amount", stage: "stage", probability: "probability", close: "closeDate" }, ["amount", "probability"]), ...(has(v, "account") ? { accountId: guid(v.account) ?? null } : {}), ...ownerBody(v) }),
  },
  activities: {
    path: "api/activities/activities",
    // `date` is the due DATE. Send `dueAt` (ISO date-time) when the person sets a time; leave it out for an all-day item.
    toRow: (r) => ({ ...base(r), type: str(r.type), subject: str(r.subject), date: date(r.due), dueAt: str(r.dueAt), account: str(r.accountId), status: str(r.status), owner: str(r.ownerName), ownerId: str(r.ownerId) }),
    toBody: (v) => ({ ...pick(v, { type: "type", subject: "subject", date: "due", dueAt: "dueAt", status: "status" }), ...(has(v, "account") ? { accountId: guid(v.account) ?? null } : {}), ...ownerBody(v) }),
  },
  cases: {
    path: "api/service/cases",
    toRow: (r) => ({ ...base(r), title: str(r.title), account: str(r.accountId), priority: str(r.priority), channel: str(r.channel), status: str(r.status), due: date(r.due), owner: str(r.ownerName), ownerId: str(r.ownerId) }),
    toBody: (v) => ({ ...pick(v, { title: "title", priority: "priority", channel: "channel", status: "status", due: "due" }), ...(has(v, "account") ? { accountId: guid(v.account) ?? null } : {}), ...ownerBody(v) }),
  },
  competitors: {
    path: "api/sales/competitors",
    toRow: (r) => ({ ...base(r), name: str(r.name), website: str(r.website), pricing: str(r.pricing), share: nz(r.sharePct), threat: str(r.threat), strengths: str(r.strengths), weaknesses: str(r.weaknesses) }),
    toBody: (v) => pick(v, { name: "name", website: "website", pricing: "pricing", share: "sharePct", threat: "threat", strengths: "strengths", weaknesses: "weaknesses" }, ["share"]),
  },
  /** Sales team = the Admin service's TeamMember. `userId` is the login (JWT sub) that records owned by this person carry as ownerId. */
  team: {
    path: "api/admin/team",
    toRow: (r) => ({ ...base(r), name: str(r.name), userId: str(r.userId), email: str(r.email), phone: str(r.phone), role: str(r.role), region: str(r.region), quota: nz(r.quota), status: str(r.status) }),
    toBody: (v) => pick(v, { name: "name", email: "email", phone: "phone", role: "role", region: "region", quota: "quota", status: "status" }, ["quota"]),
  },
};

/* ------------------------------------------------------------------ client */

export interface ClientOptions {
  transport: Transport;
  getToken: () => string | undefined | Promise<string | undefined>;
  language?: string;
  /** Called on 401 so the app can send the person to sign in again. */
  onUnauthorized?: () => void;
}

export function createCrmClient(opts: ClientOptions) {
  async function call(method: Method, path: string, body?: unknown, extra: Record<string, string> = {}) {
    const token = await opts.getToken();
    const headers: Record<string, string> = { Authorization: `Bearer ${token ?? ""}`, "Content-Type": "application/json", ...extra };
    if (opts.language) headers["Accept-Language"] = opts.language;
    try {
      const { data } = await opts.transport.send(method, path, body, headers);
      return data;
    } catch (e) {
      if (e instanceof CrmApiError && e.status === 401) opts.onUnauthorized?.();
      throw e;
    }
  }
  const map = (src: string): EntityMap => {
    const m = ENTITIES[src];
    if (!m) throw new Error(`No backend mapping for "${src}" yet. Add it to ENTITIES.`);
    return m;
  };

  return {
    /** One page of rows plus the total. */
    async list(src: string, page = 1, pageSize = 50, filters: Record<string, string> = {}, q?: string) {
      const m = map(src);
      const qs = new URLSearchParams({ page: String(page), pageSize: String(pageSize), ...filters, ...(q ? { q } : {}) });
      const data = await call("GET", `${m.path}?${qs}`);
      return { rows: (data?.items ?? []).map(m.toRow) as Row[], total: Number(data?.total ?? 0) };
    },
    /** Every row, page by page, up to `max`. Use for the lookups behind the dropdowns. */
    async listAll(src: string, max = 2000, filters: Record<string, string> = {}) {
      const out: Row[] = [];
      for (let page = 1; out.length < max; page++) {
        const { rows, total } = await this.list(src, page, 200, filters);
        out.push(...rows);
        if (rows.length < 200 || out.length >= total) break;
      }
      return out;
    },
    /** One record by its GUID (GET {path}/{id}). Used to refresh a row after a live "changed" message. */
    async get(src: string, id: string): Promise<Row> {
      const m = map(src);
      return m.toRow(await call("GET", `${m.path}/${id}`));
    },
    async create(src: string, values: Record<string, Val>): Promise<Row> {
      const m = map(src);
      return m.toRow(await call("POST", m.path, m.toBody(values)));
    },
    /** `row.version` is sent as If-Match so two people editing the same record do not overwrite each other. */
    async update(src: string, row: Row, values: Record<string, Val>): Promise<Row> {
      const m = map(src);
      const headers: Record<string, string> = row.version ? { "If-Match": `"${row.version}"` } : {};
      return m.toRow(await call("PATCH", `${m.path}/${row.id}`, m.toBody(values), headers));
    },
    async remove(src: string, row: Row) {
      await call("DELETE", `${map(src).path}/${row.id}`);
    },
    /** For workflow buttons (Send quote, Escalate case, Reschedule...). `path` is relative to the record: "reschedule", "send"... */
    async action(src: string, row: Row, path: string, body?: unknown): Promise<Row> {
      const m = map(src);
      return m.toRow(await call("POST", `${m.path}/${row.id}/${path}`, body ?? {}));
    },
  };
}

export type CrmClient = ReturnType<typeof createCrmClient>;